// AES-256-GCM password encryption for API requests.
//
// Wire format: base64( iv || ciphertext || tag ) - 12 random IV bytes, then the
// ciphertext with SubtleCrypto's 16-byte GCM tag appended. The IV is new on every
// call, so the backend must decrypt; it cannot compare ciphertexts.
//
// The key is inlined into the bundle at build time and is therefore public. This
// keeps plaintext passwords out of request bodies, URLs and server logs; it is not
// a substitute for TLS.

export async function encryptPassword(password) {
  const keyHex = process.env.PASSWORD_ENCRYPTION_KEY
  const crypto = (typeof window !== "undefined" ? window : globalThis).crypto;

  if (!keyHex) {
    throw new Error("Password encryption is unavailable: PASSWORD_ENCRYPTION_KEY is not set.");
  }

  if (!crypto || !crypto.subtle) {
    throw new Error(
      "Password encryption is unavailable: window.crypto.subtle is missing. " +
      "It needs a secure context (https:// or localhost) and a browser with Web Crypto support."
    );
  }

  const keyBytes = Uint8Array.from(keyHex.match(/../g).map(h => parseInt(h, 16)));
  const key = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-GCM" }, false, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv }, key, new TextEncoder().encode(password)
  );

  const payload = new Uint8Array(iv.length + ciphertext.byteLength);
  payload.set(iv, 0);
  payload.set(new Uint8Array(ciphertext), iv.length);

  return btoa(String.fromCharCode(...payload));
}

export default encryptPassword;
